export enum CardType {
  COUNTER = "counter",
  FLAGS = "flags",
  CASE_SUMMARY = "case_summary",
  MONEY_CAPACITY = "money_capacity",
  BULLET_LIST = "bullet_list",
}

export enum FlagSeverity {
  DEADLINE = "deadline",
  FINANCIAL = "financial",
  FOLLOWUP = "followup",
  LEGAL = "legal",
}

export enum BulletListVariant {
  POSITIVE = "positive",
  WARNING = "warning",
}

export enum TagVariant {
  DANGER = "danger",
  INFO = "info",
  NEUTRAL = "neutral",
}

export interface FlagItem {
  id: string;
  severity: FlagSeverity;
  title: string;
  description: string;
}

export interface TagItem {
  label: string;
  variant: TagVariant;
}

interface BaseCard {
  id: string;
  type: CardType;
}

export interface CounterCard extends BaseCard {
  type: CardType.COUNTER;
  title: string;
  theirAmount: number | null;
  ourAmount: number | null;
  currency?: string; // defaults to 'USD'
}

export interface FlagsCard extends BaseCard {
  type: CardType.FLAGS;
  flags: FlagItem[];
}

export enum PanelStatus {
  COMPLETED = "completed",
  PROCESSING = "processing",
  STALLED = "stalled",
  ERROR = "error",
  NOT_FOUND = "not_found",
}

export interface CaseSummaryCard extends BaseCard {
  type: CardType.CASE_SUMMARY;
  sectionLabel: string;
  entityName: string;
  fileNumber: string;
  funderLabel: string;
  description: string;
  tags: TagItem[];
}

export interface MoneyCapacityCard extends BaseCard {
  type: CardType.MONEY_CAPACITY;
  sectionLabel: string;
  rows: { label: string; value: string }[];
  note?: string;
  warnings?: string[] | null;
}

export interface BulletListCard extends BaseCard {
  type: CardType.BULLET_LIST;
  title: string;
  variant: BulletListVariant;
  items: string[];
}

export type PolicySeverity = "block" | "critical" | "warn" | "suggest";

export type T_Policy = {
  key: string;
  name: string;
  description: string;
  severity: PolicySeverity;
};

export type SmartNegotiationCard = CounterCard | FlagsCard | CaseSummaryCard | MoneyCapacityCard | BulletListCard;

export interface SmartNegotiationPanelData {
  title: string;
  subtitle: string;
  cards: SmartNegotiationCard[];
  status: PanelStatus;
}

// negotiation-offer.types.ts
//
// Types are modeled directly off real sample responses for every endpoint
// (context, offers/generate, offers/preview-custom, offers/save-custom,
// offers/saved-custom) — not the design mock's fake local data.
//
// Two spots are best-effort rather than confirmed:
//   1. `terms` on T_NegotiationOffer — confirmed for "deferral", "restructure"
//      and "discounted_frequency_change" against a real sample. "settlement"
//      and "combined" are inferred from the same naming convention used
//      elsewhere in the payload and may need adjusting once you can hit the
//      live endpoint with those offer types.
//   2. T_ClientGainLegacy — an older client_gain shape seen on some saved
//      custom offers. Confirmed deprecated (new responses always use
//      T_ClientGainMetrics), kept here only so old saved records don't
//      break the type.

export type T_OfferType = "settlement" | "restructure" | "combined" | "deferral" | "discounted_frequency_change";

export type T_AcceptanceLevel = "High" | "Medium" | "Low";

export type T_PaymentFrequency = "daily" | "weekly" | "monthly";

export type T_ContextFieldSource = "provided" | "estimated";

// ---------------------------------------------------------------------------
// Negotiation context — POST/GET /v2/negotiation/context
// ---------------------------------------------------------------------------

export type T_ContextFieldSources = {
  max_settlement_amount: T_ContextFieldSource;
  max_weekly_amount: T_ContextFieldSource;
  total_weeks: T_ContextFieldSource;
  deferral_weeks: T_ContextFieldSource;
};

export type T_NegotiationContext = {
  appkey: string;
  loan_id: string;
  max_settlement_amount: number;
  max_weekly_amount: number;
  total_weeks: number;
  deferral_weeks: number;
  context_source: T_ContextFieldSource;
  context_field_sources: T_ContextFieldSources;
  uncertainty_note: string | null;
};

export type T_NegotiationContextRequest = {
  application_id: string;
  loan_id: string;

  max_settlement_amount?: number;
  max_weekly_amount?: number;
  total_weeks?: number;
  deferral_weeks?: number;
};

export type T_NegotiationBaseRequest = {
  application_id: string;
  loan_id: string;
  thread_id?: string;
  message_id?: string;
};

export type T_ClientGainMetric = {
  key: string;
  label: string;
  unit: "currency" | "currency_per_week" | "weeks" | "boolean" | string;
  before: number | boolean | null;
  after: number | boolean | null;
  change: number | null;
  change_pct: number | null;
};

export type T_ClientGainMetrics = {
  metrics: T_ClientGainMetric[];
};

/** Deprecated — only present on some older saved-custom records. */
export type T_ClientGainLegacy = {
  dollar_savings: number | null;
  time_change_weeks: number | null;
  cash_flow_relief_per_period: number | null;
  meaningfully_reduces_nsf_risk: boolean | null;
};

export type T_ClientGain = T_ClientGainMetrics | T_ClientGainLegacy;

export function isClientGainMetrics(gain: T_ClientGain | null | undefined): gain is T_ClientGainMetrics {
  return !!gain && Array.isArray((gain as T_ClientGainMetrics).metrics);
}

export type T_OfferDetail = {
  summary: string;
  breakdown: Record<string, string | number>;
};

export type T_NegotiationOffer = {
  offer_type: T_OfferType;

  terms: Record<string, any>;
  calculated_gap: number | null;
  acceptance_level: T_AcceptanceLevel;
  reason: string;
  client_gain: T_ClientGain;
  generated_offer_detail: T_OfferDetail;
  select_when: string;
  dont_select_when: string;
};

export type T_GenerateOffersResponse = {
  offers: T_NegotiationOffer[];
  context_uncertainty_note: string | null;
};

export type T_CustomOfferFormValues = {
  lump_sum: number;
  periodic_amount: number;
  total_period: number;
  payment_frequency: T_PaymentFrequency;
  deferral_weeks: number;
};

export type T_CustomOfferRequest = T_NegotiationBaseRequest & Partial<T_CustomOfferFormValues>;

export type T_CustomOfferResult = {
  calculated_gap: number | null;
  acceptance_level: T_AcceptanceLevel;
  reason: string;
  client_gain: T_ClientGain;
  generated_customized_offer: T_OfferDetail;
};

export type T_SavedCustomOffer = T_CustomOfferResult &
  T_CustomOfferFormValues & {
    id: number;
    appkey: string;
    loan_id: string;
    thread_id: string | null;
    saved_at: string | null;
  };

export type T_OfferDisplayDetail = {
  summary: string;
  breakdown: Record<string, string | number>;
  acceptance_level: T_AcceptanceLevel;
  reason: string;
  calculated_gap: number | null;
  client_gain: T_ClientGain;
  select_when?: string;
  dont_select_when?: string;
};
